// @mui
import {
  Box,
  Card,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
  timelineOppositeContentClasses,
} from "@mui/lab";
// components
import Iconify from "../../../../../components/iconify";

import { fDate } from "../../../../../utils/formatTime";
import { useLocales } from "../../../../../locales";

function BreachTimeline({ breaches }) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const { translate } = useLocales();

  if (!breaches || breaches.length === 0) return null;

  const sortedBreaches = [...breaches].sort(
    (a, b) => new Date(b.breachDate) - new Date(a.breachDate)
  );

  return (
    <Timeline
      position={isDesktop ? "alternate" : "right"}
      sx={{
        [`& .${timelineOppositeContentClasses.root}`]: {
          ...(!isDesktop && {
            flex: 0,
            padding: 0,
            display: "none",
          }),
        },
      }}
    >
      {sortedBreaches.map((breach) => {
        const isCritical = breach.dataClasses.some((dataClass) =>
          dataClass.toLowerCase().includes("password")
        );

        const formattedDate = fDate(breach.breachDate, "MMMM yyyy");

        return (
          <TimelineItem key={breach.uuid}>
            <TimelineOppositeContent
              sx={{
                m: 0,
                pt: 2.5,
                px: { xs: 0, md: 3 },
                color: "text.secondary",
                flex: 1,
                flexBasis: "50%",
                minWidth: 0,
              }}
              variant="body2"
            >
              {formattedDate}
            </TimelineOppositeContent>

            <TimelineSeparator sx={{ alignItems: "center" }}>
              <TimelineConnector sx={{ flexGrow: 0, height: "20px" }} />
              <TimelineDot
                sx={{
                  bgcolor: isCritical ? "error.main" : "grey.400",
                  boxShadow: "none",
                  m: 0,
                }}
              />
              <TimelineConnector />
            </TimelineSeparator>

            <TimelineContent
              sx={{
                py: 0,
                pb: 3,
                px: { xs: 2, md: 3 },
                flex: 1,
                flexBasis: { xs: "100%", md: "50%" },
                minWidth: 0,
              }}
            >
              {!isDesktop && (
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    mb: 1,
                    mt: 0.5,
                    textTransform: "lowercase",
                  }}
                >
                  {formattedDate}
                </Typography>
              )}

              <Card
                variant="outlined"
                sx={{
                  maxWidth: 450,
                  width: "100%",
                  borderRadius: 2,
                  boxShadow: "none",
                  borderColor: "divider",
                  textAlign: "left",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    p: 2,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.neutral",
                    overflow: "hidden",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    noWrap
                    sx={{ fontWeight: 600, mr: 1 }}
                  >
                    {breach.domain || breach.name}
                  </Typography>

                  {isCritical && (
                    <Iconify
                      icon="eva:alert-circle-fill"
                      sx={{
                        color: "error.main",
                        width: 24,
                        height: 24,
                        flexShrink: 0,
                      }}
                    />
                  )}
                </Stack>

                <Stack spacing={1.5} sx={{ p: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {translate("monitoringPage.compromisedData")}
                  </Typography>

                  <Box
                    component="ul"
                    sx={{
                      pl: 2.5,
                      m: 0,
                      listStyleType: "disc",
                    }}
                  >
                    {breach.dataClasses.map((item) => {
                      const isPassword = item
                        .toLowerCase()
                        .includes("password");

                      const translatedItem = translate(
                        `monitoringPage.dataClasses.${item}`,
                        { defaultValue: item }
                      );

                      return (
                        <Typography
                          component="li"
                          key={item}
                          variant="body2"
                          sx={{
                            mb: 0.5,
                            color: isPassword ? "error.main" : "text.secondary",
                            fontWeight: isPassword ? 700 : 400,
                            "&::marker": {
                              color: isPassword
                                ? "error.main"
                                : "text.disabled",
                            },
                          }}
                        >
                          {translatedItem}
                        </Typography>
                      );
                    })}
                  </Box>
                </Stack>
              </Card>
            </TimelineContent>
          </TimelineItem>
        );
      })}
    </Timeline>
  );
}

export default BreachTimeline;
